import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    signInWithPopup,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    confirmPasswordReset as firebaseConfirmPasswordReset,
    updateEmail as firebaseUpdateEmail
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

// User role type
export type UserRole = 'admin' | 'affiliate';

// Extended user type with role
export interface AppUser {
    uid: string;
    email: string | null;
    role: UserRole;
    permissions: string[];
    companyName?: string;
    country?: string;
}

// Auth context type
interface AuthContextType {
    user: AppUser | null;
    loading: boolean;
    error: string | null;
    signIn: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    sendMagicLink: (email: string) => Promise<void>;
    signInWithMagicLink: (email: string, href: string) => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    confirmResetPassword: (oobCode: string, newPassword: string) => Promise<void>;
    updateUserEmail: (newEmail: string) => Promise<void>;
    isAdmin: boolean;
    isAffiliate: boolean;
    hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALL_ADMIN_PERMISSIONS = [
    'manage_users',
    'manage_finance',
    'view_analytics',
    'manage_settings',
];

function getEffectivePermissions(role: UserRole | undefined, userData: Record<string, unknown>): string[] {
    if (role !== 'admin') {
        return [];
    }

    const hasPermissionsField = Object.prototype.hasOwnProperty.call(userData, 'permissions');
    if (!hasPermissionsField) {
        return [...ALL_ADMIN_PERMISSIONS];
    }

    return Array.isArray(userData.permissions) ? userData.permissions : [];
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AppUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Listen for auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
            if (firebaseUser) {
                try {
                    // Fetch user document from Firestore to get role
                    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const role = (userData.role as UserRole | undefined) || 'affiliate';
                        setUser({
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            role,
                            permissions: getEffectivePermissions(role, userData as Record<string, unknown>),
                            companyName: userData.company_details?.name,
                            country: userData.country,
                        });
                    } else {
                        // User exists in Auth but not in Firestore. 
                        // For Google/Magic Link, we might want to create a default affiliate profile or just let them exist without one until confirmed.
                        console.warn('User document not found in Firestore');
                        setUser({
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            role: 'affiliate',
                            permissions: [],
                        });
                    }
                } catch (err) {
                    console.error('Error fetching user data:', err);
                    setError('Failed to load user data');
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Sign in with email/password
    const signIn = async (email: string, password: string) => {
        setError(null);
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err: any) {
            console.error('Sign in error:', err);
            if (err.code === 'auth/invalid-credential') {
                setError('Invalid email or password');
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many failed attempts. Please try again later.');
            } else {
                setError('Failed to sign in. Please try again.');
            }
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Sign in with Google
    const signInWithGoogle = async () => {
        setError(null);
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
        } catch (err: any) {
            console.error('Google sign in error:', err);
            setError('Failed to sign in with Google');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Send Magic Link
    const sendMagicLink = async (email: string) => {
        setError(null);
        setLoading(true);
        try {
            const actionCodeSettings = {
                // URL you want to redirect back to. The domain (www.example.com) for this
                // URL must be in the authorized domains list in the Firebase Console.
                url: window.location.origin + '/login',
                handleCodeInApp: true,
            };
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            window.localStorage.setItem('emailForSignIn', email);
        } catch (err: any) {
            console.error('Magic link error:', err);
            setError('Failed to send magic link');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Complete Magic Link Sign In
    const signInWithMagicLink = async (email: string, href: string) => {
        setError(null);
        setLoading(true);
        try {
            if (isSignInWithEmailLink(auth, href)) {
                await signInWithEmailLink(auth, email, href);
                window.localStorage.removeItem('emailForSignIn');
            } else {
                throw new Error('Invalid magic link');
            }
        } catch (err: any) {
            console.error('Magic link sign in error:', err);
            setError('Failed to sign in with magic link');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Sign out
    const signOut = async () => {
        setError(null);
        try {
            await firebaseSignOut(auth);
            setUser(null);
        } catch (err: any) {
            console.error('Sign out error:', err);
            setError('Failed to sign out');
            throw err;
        }
    };

    // Reset password
    const resetPassword = async (email: string) => {
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (err: any) {
            console.error('Password reset error:', err);
            if (err.code === 'auth/user-not-found') {
                setError('No account found with this email');
            } else {
                setError('Failed to send reset email');
            }
            throw err;
        }
    };

    // Confirm Reset password
    const confirmResetPassword = async (oobCode: string, newPassword: string) => {
        setError(null);
        try {
            await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
        } catch (err: any) {
            console.error('Confirm password reset error:', err);
            if (err.code === 'auth/invalid-action-code' || err.code === 'auth/expired-action-code') {
                setError('The reset link is invalid or has expired');
            } else {
                setError('Failed to reset password');
            }
            throw err;
        }
    };

    // Update user email
    const updateUserEmail = async (newEmail: string) => {
        setError(null);
        try {
            if (!auth.currentUser || !user) throw new Error('No user logged in');
            
            // Update in Firebase Auth
            await firebaseUpdateEmail(auth.currentUser, newEmail);
            
            // Update in Firestore
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, { email: newEmail });
            
            // Update local state
            setUser({ ...user, email: newEmail });
        } catch (err: any) {
            console.error('Update email error:', err);
            if (err.code === 'auth/requires-recent-login') {
                setError('Please log out and log back in to change your email address for security reasons.');
            } else if (err.code === 'auth/email-already-in-use') {
                setError('This email address is already in use by another account.');
            } else {
                setError('Failed to update email address.');
            }
            throw err;
        }
    };

    const hasPermission = (permission: string) => {
        if (!user || user.role !== 'admin') return false;

        return user.permissions.includes(permission);
    };

    const value: AuthContextType = {
        user,
        loading,
        error,
        signIn,
        signInWithGoogle,
        sendMagicLink,
        signInWithMagicLink,
        signOut,
        resetPassword,
        confirmResetPassword,
        updateUserEmail,
        isAdmin: user?.role === 'admin',
        isAffiliate: user?.role === 'affiliate',
        hasPermission,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Custom hook to use auth context
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
